/**
 * The scan sends the matter Info tab's abbreviated labels — "Adverse", "Clients",
 * "Related" — which do not identify the field they came from. The matter edit form
 * names them in full, and a result row has to say which field matched, so the display
 * layer owns the naming and the API is left alone.
 *
 * Keys are the scan's stable `ConflictFieldId`, not its labels. The labels are display
 * wording and will be reworded; keying on them is what let sc-47663's pill render empty
 * when the row contract moved underneath it. A field arriving without a mapping still
 * renders, under its own id, in the other group.
 */

import type { ConflictFieldId } from '../models/conflictTypes';

export type ConflictFieldGroupKey = 'own' | 'adverse' | 'other';

export const CONFLICT_FIELD_LABELS: Record<ConflictFieldId, string> = {
  clientName: 'Client name',
  clientFirstName: 'First name',
  clientLastName: 'Last name',
  clientDisplayName: 'Display name',
  clientCompanyName: 'Company name',
  clientEmail: 'Client email',
  matterName: 'Matter name',
  matterNumber: 'Matter number',
  matterAdverse: 'Adverse Parties',
  matterOpposingCounsel: 'Opposing Counsel',
  matterClients: 'Related Client Entities',
  matterRelated: 'Related Adverse Entities',
  matterReferralSource: 'Referral source',
  matterNotes: 'Notes',
  matterBillingInstructions: 'Billing instructions',
  timeEntryDescription: 'Time entry description',
};

/** The one field a permission can withhold, named once so the gate cannot drift. */
export const CONFLICT_BILLING_INSTRUCTIONS_FIELD: ConflictFieldId =
  'matterBillingInstructions';

/**
 * Green is who the firm acts for, red is who it acts against, grey is every other
 * searched field. `matterClients` is client-side and `matterRelated` is adverse-side,
 * which is the opposite of what their abbreviated server labels suggest.
 */
export const CONFLICT_FIELD_GROUPS: Array<{
  key: ConflictFieldGroupKey;
  fields: ConflictFieldId[];
}> = [
  {
    key: 'own',
    fields: [
      'clientName',
      'clientFirstName',
      'clientLastName',
      'clientDisplayName',
      'clientCompanyName',
      'clientEmail',
      'matterName',
      'matterNumber',
      'matterClients',
    ],
  },
  {
    key: 'adverse',
    fields: ['matterAdverse', 'matterOpposingCounsel', 'matterRelated'],
  },
  {
    key: 'other',
    fields: [
      'matterReferralSource',
      'matterNotes',
      'matterBillingInstructions',
      'timeEntryDescription',
    ],
  },
];

const GROUP_BY_FIELD = {} as Record<ConflictFieldId, ConflictFieldGroupKey>;
CONFLICT_FIELD_GROUPS.forEach((group) => {
  group.fields.forEach((field) => {
    GROUP_BY_FIELD[field] = group.key;
  });
});

/** Tailwind classes per group. Kept here so a pill and its legend cannot drift apart. */
export const CONFLICT_GROUP_CLASSES: Record<ConflictFieldGroupKey, string> = {
  own: 'bg-green-50 text-green-800 border-green-200',
  adverse: 'bg-red-50 text-red-800 border-red-200',
  other: 'bg-gray-50 text-gray-600 border-gray-300',
};

export function conflictFieldLabel(field: ConflictFieldId): string {
  return CONFLICT_FIELD_LABELS[field] ?? field;
}

export function conflictFieldGroup(field: ConflictFieldId): ConflictFieldGroupKey {
  return GROUP_BY_FIELD[field] ?? 'other';
}

/** Every field the scan reads, in the order the filter and the preview panel list them. */
export const CONFLICT_SEARCHED_FIELDS: ConflictFieldId[] =
  CONFLICT_FIELD_GROUPS.reduce(
    (all: ConflictFieldId[], group) => all.concat(group.fields),
    [],
  );

/** The nine matter fields, in display order, for the preview panel. */
export const CONFLICT_MATTER_FIELDS: ConflictFieldId[] = [
  'matterName',
  'matterNumber',
  'matterClients',
  'matterAdverse',
  'matterOpposingCounsel',
  'matterRelated',
  'matterReferralSource',
  'matterNotes',
  'matterBillingInstructions',
];

/** The six client fields the scan reads. */
export const CONFLICT_CLIENT_FIELDS: ConflictFieldId[] = [
  'clientName',
  'clientFirstName',
  'clientLastName',
  'clientDisplayName',
  'clientCompanyName',
  'clientEmail',
];

/** What the clean-result and scope lines say the check covered. */
export const CONFLICT_SCOPE_SENTENCE =
  'client name, first, last, display and company name and email address; ' +
  'matter name and number; all four conflict fields (Adverse Parties, ' +
  'Opposing Counsel, Related Client Entities, Related Adverse Entities); ' +
  'referral source, Notes and Billing instructions — including archived ' +
  'clients and matters';
